import axios, { AxiosResponseTransformer } from 'axios'
import { getHeader, isEmpty } from '../../../utils/tsUtils/utils'
var JSONbig = require('json-bigint')

export interface ReportsFetchData {
  from_date?: string
  to_date?: string
  report_date?: string
  outlet_id?: bigint
  pageNumber?: number
  limit?: number
  bill_status?: string
  user_id?: bigint
  kitchen_counter_id?: bigint
  waiter_id?: bigint
  by_business_name?: boolean
  sub_business_name?: string
  commission_mode?: number
}

export interface ReportsFetchResponse {
  Total_Bills?: number
  Record_Date?: string
  Total_Disc: number
  Total_Cess: number
  Total_GST: number
  Total_Sales?: number
  Total_Qty: number
  Bill_Num?: number
  Bill_Id?: string
  Bill_Date?: string
  Total?: number
  PaymentMode?: string
  Terminal?: string
  Date?: string
  IName?: string
  IPrice?: number
  item_name?: string
  item_id?: bigint
  opening_stock?: number
  purchase_stock?: number
  sold_stock?: number
  closing_stock?: number
  Outlet_Name?: string
}

export interface ReportsResponse {
  data: ReportsFetchResponse
  total: number
  limit: number
  page: number
}
export class ReportService {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  async fetchBusinessSumary(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      let url = `${this.baseUrl}hotel_billing/reports/business/summary`
      if (payload.from_date && payload.to_date) {
        url = `${url}?from_date=${payload.from_date}&to_date=${payload.to_date}`
      }
      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => {
            return data
          },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsFetchResponse = response.data.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchOutletSummary(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      let url = `${this.baseUrl}hotel_billing/reports/business/outlet/summary?from_date=${payload.from_date}&to_date=${payload.to_date}`
      
      // Add outlet_id only if it exists
      if (payload.outlet_id) {
        url += `&outlet_id=${payload.outlet_id}`
      }
      
      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => {
            return data
          },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsFetchResponse = response.data.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchOutletwiseSummary(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      let url =
        payload !== undefined && payload !== null
          ? `${this.baseUrl}hotel_billing/reports/outlet/summary?from_date=${payload.from_date}&to_date=${payload.to_date}`
          : `${this.baseUrl}hotel_billing/reports/outlet/summary`
      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => {
            return data
          },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsFetchResponse = response.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchOutletwiseSummaryForFranchise(
    payload: ReportsFetchData
  ): Promise<any> {
    try {
      let header = getHeader()
      let url =
        payload.sub_business_name !== undefined &&
        payload.sub_business_name !== ''
          ? `${this.baseUrl}hotel_billing/reports/franchise/outlet/summary?from_date=${payload.from_date}&to_date=${payload.to_date}&by_business_name=${payload.by_business_name}&page=${payload.pageNumber}&limit=${payload.limit}&sub_business_name=${payload.sub_business_name}`
          : `${this.baseUrl}hotel_billing/reports/franchise/outlet/summary?from_date=${payload.from_date}&to_date=${payload.to_date}&by_business_name=${payload.by_business_name}&page=${payload.pageNumber}&limit=${payload.limit}`
      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => {
            return data
          },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsFetchResponse[] = response.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchBillwiseReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()

      // Build base URL
      let baseUrl = `${this.baseUrl}hotel_billing/reports/list_bills/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`
      
      // Add outlet_id only if it exists
      if (payload.outlet_id) {
        baseUrl += `&outlet_id=${payload.outlet_id}`
      }

      let url = payload.user_id
        ? `${baseUrl}&user_id=${payload.user_id}&by_user=true`
        : baseUrl

      if (payload.bill_status !== undefined && !isEmpty(payload.bill_status)) {
        url = `${url}&bill_status=${payload.bill_status}`
      }
      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => {
            return data
          },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsResponse = response.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchDaywiseReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      
      // Build base URL
      let url = `${this.baseUrl}hotel_billing/reports/list_datewise_bills/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`
      
      // Add outlet_id only if it exists
      if (payload.outlet_id) {
        url += `&outlet_id=${payload.outlet_id}`
      }

      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => {
            return data
          },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsResponse = response.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchItemWiseReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      
      // Build base URL
      let baseUrl = `${this.baseUrl}hotel_billing/reports/list_bill_items/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`
      
      // Add outlet_id only if it exists
      if (payload.outlet_id) {
        baseUrl += `&outlet_id=${payload.outlet_id}`
      }

      let url = payload.kitchen_counter_id
        ? `${baseUrl}&kitchen_counter_id=${payload.kitchen_counter_id}&by_kitchen_counter=true`
        : baseUrl

      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => {
            return data
          },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsResponse = response.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchInventoryReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      
      // Build base URL
      let url = `${this.baseUrl}hotel_billing/reports/inventory?report_date=${payload.report_date}&page=${payload.pageNumber}&limit=${payload.limit}`
      
      // Add outlet_id only if it exists
      if (payload.outlet_id) {
        url += `&outlet_id=${payload.outlet_id}`
      }

      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => {
            return data
          },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsResponse = response.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchUserwiseReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      
      // Build base URL
      let url = `${this.baseUrl}hotel_billing/reports/user/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`
      
      // Add outlet_id only if it exists
      if (payload.outlet_id) {
        url += `&outlet_id=${payload.outlet_id}`
      }

      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => JSONbig.parse(data),
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsResponse = response.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchCounterWiseReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      
      // Build base URL
      let url = `${this.baseUrl}hotel_billing/reports/counter/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`
      
      // Add outlet_id only if it exists
      if (payload.outlet_id) {
        url += `&outlet_id=${payload.outlet_id}`
      }

      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => JSONbig.parse(data),
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsResponse = response.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchWriteOffReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      
      // Build base URL
      let url = `${this.baseUrl}hotel_billing/reports/writeoff/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`
      
      // Add outlet_id only if it exists
      if (payload.outlet_id) {
        url += `&outlet_id=${payload.outlet_id}`
      }

      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => {
            return data
          },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsResponse = response.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchDuplicateBillReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      
      // Build base URL
      let url = `${this.baseUrl}hotel_billing/reports/duplicate_bill/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`
      
      // Add outlet_id only if it exists
      if (payload.outlet_id) {
        url += `&outlet_id=${payload.outlet_id}`
      }

      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => {
            return data
          },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsResponse = response.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }
  async fetchPaymentwiseReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      
      // Build base URL
      let url = `${this.baseUrl}hotel_billing/reports/payment_mode/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`
      
      // Add outlet_id only if it exists
      if (payload.outlet_id) {
        url += `&outlet_id=${payload.outlet_id}`
      }

      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => {
            return data
          },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      const responseData: ReportsResponse = response.data.data
      return responseData
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

async downloadReports(payload: any): Promise<any> {
    try {
        let header = getHeader();
        let baseUrlPart = `${this.baseUrl}hotel_billing/reports/`;
        let url = "";
       
        switch (payload.type) {
            case "inventory": {
                url = `${baseUrlPart}inventory?report_date=${payload.report_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                if (payload.outlet_id) {
                    url += `&outlet_id=${payload.outlet_id}`;
                }
                break;
            }
            case "billwise": {
                let billwiseBaseUrl = `${baseUrlPart}list_bills/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                if (payload.outlet_id) {
                    billwiseBaseUrl += `&outlet_id=${payload.outlet_id}`;
                }
                url = payload.user_id ? 
                    `${billwiseBaseUrl}&user_id=${payload.user_id}&by_user=true` :
                    billwiseBaseUrl;
                break;
            }
            case "daywise": {
                url = `${baseUrlPart}list_datewise_bills/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                if (payload.outlet_id) {
                    url += `&outlet_id=${payload.outlet_id}`;
                }
                break;
            }
            case "itemwise": {
                let itemwiseBaseUrl = `${baseUrlPart}list_bill_items/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                if (payload.outlet_id) {
                    itemwiseBaseUrl += `&outlet_id=${payload.outlet_id}`;
                }
                url = payload.kitchen_counter_id ? 
                    `${itemwiseBaseUrl}&kitchen_counter_id=${payload.kitchen_counter_id}&by_kitchen_counter=true` :
                    itemwiseBaseUrl;
                break;
            }
            case "duplicatebill": {
                url = `${baseUrlPart}duplicate_bill/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                if (payload.outlet_id) {
                    url += `&outlet_id=${payload.outlet_id}`;
                }
                break;
            }
            case "writeoff": {
                url = `${baseUrlPart}writeoff/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                if (payload.outlet_id) {
                    url += `&outlet_id=${payload.outlet_id}`;
                }
                break;
            }
            case "userwise": {
                url = `${baseUrlPart}user/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                if (payload.outlet_id) {
                    url += `&outlet_id=${payload.outlet_id}`;
                }
                break;
            }
            case "paymentwise": {
                url = `${baseUrlPart}payment_mode/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                if (payload.outlet_id) {
                    url += `&outlet_id=${payload.outlet_id}`;
                }
                break;
            }
            case "counterwise": {
                url = `${baseUrlPart}counter/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                if (payload.outlet_id) {
                    url += `&outlet_id=${payload.outlet_id}`;
                }
                break;
            }
                case "userwisebill": {
                        url = `${baseUrlPart}${payload.url}/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}&user_id=${payload.user_id}&by_user=true`;
                        if (payload.outlet_id) {
                            url += `&outlet_id=${payload.outlet_id}`;
                        }
                        break;
                }
             case "counterwisereportbill": {
                     url = `${baseUrlPart}${payload.url}/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}&kitchen_counter_id=${payload.kitchen_counter_id}&by_kitchen_counter=true`;
                     if (payload.outlet_id) {
                         url += `&outlet_id=${payload.outlet_id}`;
                     }
                     break;
             }
          case "summary": {
            let summaryBaseUrl = `${baseUrlPart}${payload.url}/outlet/summary?from_date=${payload.from_date}&to_date=${payload.to_date}`;
            if (payload.outlet_id) {
              summaryBaseUrl += `&outlet_id=${payload.outlet_id}`;
            }
            url = summaryBaseUrl;
            
            break;
          }
            case "outletwise_summary": 
                url = `${baseUrlPart}outlet/summary?from_date=${payload.from_date}&to_date=${payload.to_date}`;
                break;
            
            case "outletwise_summary_franchise": {
                url = payload.sub_business_name 
                    ? `${baseUrlPart}franchise/outlet/summary?from_date=${payload.from_date}&to_date=${payload.to_date}&by_business_name=${payload.by_business_name}&page=${payload.pageNumber}&limit=${payload.limit}&sub_business_name=${payload.sub_business_name}`
                    : `${baseUrlPart}franchise/outlet/summary?from_date=${payload.from_date}&to_date=${payload.to_date}&by_business_name=${payload.by_business_name}&page=${payload.pageNumber}&limit=${payload.limit}`;
                break;
            }
            case "franchisewise_summary": {
                url = `${baseUrlPart}franchise/outlet/summary?from_date=${payload.from_date}&to_date=${payload.to_date}&by_business_name=${payload.by_business_name}&page=${payload.pageNumber}&limit=${payload.limit}`;
                break;
            }
            case "allreports": {
                url = `${baseUrlPart}${payload.url}/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                if (payload.outlet_id) {
                    url += `&outlet_id=${payload.outlet_id}`;
                }
                break;
            }
        }

        if (payload.bill_status !== undefined && !isEmpty(payload.bill_status)) {
                url = `${url}&bill_status=${payload.bill_status}`;
            }


        const response = await axios.get(url, {
            transformResponse: [
                (data, headers) => { return data },
                ...axios.defaults.transformResponse as AxiosResponseTransformer[],
            ],
            headers: header
        })
        let responseData: any = response?.data.data.data;
         // If the total limit is more than 100, fetch the remaining pages of data
        if (payload.total > 100) {
            const totalPages = Math.ceil(payload.total / 100);
            for(let i = payload.pageNumber + 1; i <= totalPages; i++) {
                payload.pageNumber = i;
                let url = "";
             switch (payload.type) {
                 case "inventory": {
                    url = `${baseUrlPart}inventory?report_date=${payload.report_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                    if (payload.outlet_id) {
                        url += `&outlet_id=${payload.outlet_id}`;
                    }
                     break;
                 }
                 case "billwise": {
                    let billwiseBaseUrl = `${baseUrlPart}list_bills/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                    if (payload.outlet_id) {
                        billwiseBaseUrl += `&outlet_id=${payload.outlet_id}`;
                    }
                    url = payload.user_id ? 
                        `${billwiseBaseUrl}&user_id=${payload.user_id}&by_user=true` :
                        billwiseBaseUrl;
                     break;
                 }
                 case "daywise": {
                    url = `${baseUrlPart}list_datewise_bills/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                    if (payload.outlet_id) {
                        url += `&outlet_id=${payload.outlet_id}`;
                    }
                     break;
                 }
                 case "itemwise": {
                    let itemwiseBaseUrl = `${baseUrlPart}list_bill_items/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                    if (payload.outlet_id) {
                        itemwiseBaseUrl += `&outlet_id=${payload.outlet_id}`;
                    }
                    url = payload.kitchen_counter_id ? 
                        `${itemwiseBaseUrl}&kitchen_counter_id=${payload.kitchen_counter_id}&by_kitchen_counter=true` :
                        itemwiseBaseUrl;
                     break;
                 }
                 case "duplicatebill": {
                    url = `${baseUrlPart}duplicate_bill/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                    if (payload.outlet_id) {
                        url += `&outlet_id=${payload.outlet_id}`;
                    }
                     break;
                 }
                 case "writeoff": {
                    url = `${baseUrlPart}writeoff/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                    if (payload.outlet_id) {
                        url += `&outlet_id=${payload.outlet_id}`;
                    }
                     break;
                 }
                 case "userwisebill": {
                        url = `${baseUrlPart}${payload.url}/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}&user_id=${payload.user_id}&by_user=true`;
                        if (payload.outlet_id) {
                            url += `&outlet_id=${payload.outlet_id}`;
                        }
                     break;
                 }
                 case "counterwisereportbill": {
                     url = `${baseUrlPart}${payload.url}/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}&kitchen_counter_id=${payload.kitchen_counter_id}&by_kitchen_counter=true`;
                     if (payload.outlet_id) {
                         url += `&outlet_id=${payload.outlet_id}`;
                     }
                     break;
                 }
                  case "summary": {
                url = `${baseUrlPart}${payload.url}/outlet/summary?from_date=${payload.from_date}&to_date=${payload.to_date}`;
                if (payload.outlet_id) {
                    url += `&outlet_id=${payload.outlet_id}`;
                }
                break;
                }
                case "allreports": {
                    url = `${baseUrlPart}${payload.url}/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`;
                    if (payload.outlet_id) {
                        url += `&outlet_id=${payload.outlet_id}`;
                    }
                     break;
                }
                 default:
                    console.error("Unsupported report type:", payload.type);
                    return;
             }
            
            
                if (payload.bill_status !== undefined && !isEmpty(payload.bill_status)) {
                url = `${url}&bill_status=${payload.bill_status}`;
                }

                const response = await axios.get(url, {
                    transformResponse: [
                        (data, headers) => { return data },
                        ...axios.defaults.transformResponse as AxiosResponseTransformer[],
                    ],
                    headers: header
                })
            
                responseData = [...responseData, ...response.data?.data.data];
      
            }
        }
        return responseData;
    } catch (error) {
        throw new Error("Failed to fetch reports");
    }
}

async fetchBillDetails(billId: string): Promise<any> {
    try {
        const header = getHeader()
        const url = `${this.baseUrl}hotel_billing/bill/billitem?id=${billId}`

        const response = await axios.get(url, {
            transformResponse: [
                (data, headers) => { return data },
                ...axios.defaults.transformResponse as AxiosResponseTransformer[],
            ],
            headers: header
        })

        return response.data
    } catch (error) {
        throw new Error("Failed to fetch bill details");
    }
}

  async fetchWaiterItemWiseReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      let url = `${this.baseUrl}hotel_billing/reports/waiter/itemwise/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&waiter_id=${payload.waiter_id}&outlet_id=${payload.outlet_id}&page=${payload.pageNumber}&limit=${payload.limit}`
      if (payload.commission_mode !== undefined && payload.commission_mode !== null) {
        url += `&commission_mode=${payload.commission_mode}`
      }
      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => { return data },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      return response.data.data
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchWaiterCommissionReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      let url = `${this.baseUrl}hotel_billing/reports/waiter/commission/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&page=${payload.pageNumber}&limit=${payload.limit}`
      if (payload.outlet_id) {
        url += `&outlet_id=${payload.outlet_id}`
      }
      if (payload.commission_mode !== undefined && payload.commission_mode !== null) {
        url += `&commission_mode=${payload.commission_mode}`
      }
      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => { return data },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      return response.data.data
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchWaiterMonthWiseReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      let url = `${this.baseUrl}hotel_billing/reports/waiter/monthwise/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&waiter_id=${payload.waiter_id}&outlet_id=${payload.outlet_id}&page=${payload.pageNumber}&limit=${payload.limit}`
      if (payload.commission_mode !== undefined && payload.commission_mode !== null) {
        url += `&commission_mode=${payload.commission_mode}`
      }
      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => { return data },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      return response.data.data
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }

  async fetchWaiterSalesReport(payload: ReportsFetchData): Promise<any> {
    try {
      let header = getHeader()
      let url = `${this.baseUrl}hotel_billing/reports/waiter/sales/outlet?from_date=${payload.from_date}&to_date=${payload.to_date}&waiter_id=${payload.waiter_id}&outlet_id=${payload.outlet_id}&page=${payload.pageNumber}&limit=${payload.limit}`
      if (payload.commission_mode !== undefined && payload.commission_mode !== null) {
        url += `&commission_mode=${payload.commission_mode}`
      }
      const response = await axios.get(url, {
        transformResponse: [
          (data, headers) => { return data },
          ...(axios.defaults.transformResponse as AxiosResponseTransformer[])
        ],
        headers: header
      })
      return response.data.data
    } catch (error) {
      throw new Error('Failed to fetch reports')
    }
  }
}